import { createFilterBuilder } from '@jebel/utils';

import {
  useRejectSchoolMemberMutation,
  UserSchoolMembershipFilter,
  UserKeyFilter,
} from 'shared/graphql';
import { useSchoolConfiguration, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

const REJECTING_MEMBER_MESSAGE_KEY = 'REJECTING_MEMBER_MESSAGE';

type Options = Parameters<typeof useRejectSchoolMemberMutation>[0];

/**
 * Custom hook to rejects a school member.
 *
 * This hook provides a function to reject a member for a school and handles
 * success and error notifications.
 *
 * @returns An object containing:
 * - `mutate`: A function to reject a member.
 * - `loading`: A boolean indicating if the mutation is in progress.
 *
 * @example
 * const { mutate, loading } = useRejectMember();
 *
 * const handleReject = async (user) => {
 *   await mutate(user);
 * };
 */
export function useRejectMember(options?: Options) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();
  const { data: school } = useSchoolConfiguration();

  const [mutation, { loading }] = useRejectSchoolMemberMutation(options);

  const mutate = async (user: UserKeyFilter) => {
    if (!school) {
      return;
    }

    try {
      showMessage(`Rejecting member "${user.email ?? user.id}".`, {
        id: REJECTING_MEMBER_MESSAGE_KEY,
      });

      const filter = createFilterBuilder<UserSchoolMembershipFilter>();

      if (user.id) {
        filter.or({ user: { id: { equals: user.id } } });
      }

      if (user.email) {
        filter.or({ user: { email: { equals: user.email } } });
      }

      await mutation({
        variables: {
          filter: filter.build(),
          school: { id: school.id },
        },
      });

      showSuccess(`Member "${user.email ?? user.id}" has been rejected`);
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message, { reportable: false });
      }
    } finally {
      dismiss(REJECTING_MEMBER_MESSAGE_KEY);
    }
  };

  return {
    mutate,
    loading,
  };
}
